import { aiGoodsStudioPromptCaseAPI } from '@/utils/api';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  normalizeTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  GOODS_STUDIO_DEFAULT_PARAMS,
  GOODS_STUDIO_COUNTRY_OPTIONS,
  GOODS_STUDIO_LANGUAGE_OPTIONS,
  GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS,
  normalizeGoodsStudioCountryStyle,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import {
  buildText2ImgCaseOwnerFilterOptions,
  resolveText2ImgCaseOwnerLabel,
} from '@/pages/ipe/workbench/utils/text2imgPromptCaseLoader';

export const GOODS_STUDIO_CASE_DEFAULT_PAGE_SIZE = 20;
export const GOODS_STUDIO_CASE_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

function normalizeCountry(value) {
  const hit = GOODS_STUDIO_COUNTRY_OPTIONS.find((item) => item.value === value);
  return hit?.value || GOODS_STUDIO_DEFAULT_PARAMS.country;
}

function normalizeLanguage(value) {
  const hit = GOODS_STUDIO_LANGUAGE_OPTIONS.find((item) => item.value === value);
  return hit?.value || GOODS_STUDIO_DEFAULT_PARAMS.language;
}

function normalizeMainImageRatio(value) {
  const hit = GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS.find((item) => item.value === value);
  return hit?.value || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio;
}

/** 将后端 CaseView 转为主图创作模板库统一结构（fallbackScope：后端未返回 caseScope 时的兜底） */
export function mapGoodsStudioPromptCaseApiItem(item, fallbackScope = 'official') {
  if (!item || typeof item !== 'object') return null;
  const name = String(item.name || '').trim() || '未命名模板';
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  const country = normalizeCountry(item.country);
  return {
    id,
    name,
    productInfo: String(item.productInfo || '').trim(),
    country,
    countryStyle: normalizeGoodsStudioCountryStyle(country, item.countryStyle),
    language: normalizeLanguage(item.language),
    mainImageRatio: normalizeMainImageRatio(item.mainImageRatio),
    coverUrl: String(item.coverUrl || item.url || '').trim(),
    scope: normalizeTitlePresetScope(item.caseScope || item.scope || fallbackScope),
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    ownerDisplayName: String(item.ownerDisplayName || '').trim(),
    sortWeight: Number(item.sortWeight) || 0,
    source: 'api',
  };
}

/**
 * 按 scope 真分页拉取主图创作模板（个人 / 团队 / 官方）。
 * includeOfficial=true 时后端会把「官方」合并进本次查询（前端模板库锁定作用域时使用）。
 */
export async function loadGoodsStudioPromptCasesPage({
  scope = DEFAULT_TITLE_PRESET_SCOPE,
  keyword = '',
  ownerUserId = '',
  page = 1,
  pageSize = GOODS_STUDIO_CASE_DEFAULT_PAGE_SIZE,
  includeOfficial = false,
} = {}) {
  const safeScope = normalizeTitlePresetScope(scope);
  const ownerId = ownerUserId != null && String(ownerUserId).trim() !== ''
    ? String(ownerUserId).trim()
    : undefined;
  const res = await aiGoodsStudioPromptCaseAPI.list({
    scope: safeScope,
    keyword: String(keyword || '').trim() || undefined,
    ownerUserId: safeScope === 'team' ? ownerId : undefined,
    page,
    pageSize,
    includeOfficial: includeOfficial ? true : undefined,
  });
  const items = (Array.isArray(res?.items) ? res.items : [])
    .map((raw) => mapGoodsStudioPromptCaseApiItem(raw, safeScope))
    .filter(Boolean);
  return {
    items,
    total: Number(res?.total) || 0,
    page: Number(res?.page) || page || 1,
    pageSize: Number(res?.pageSize) || pageSize || GOODS_STUDIO_CASE_DEFAULT_PAGE_SIZE,
    caseScope: res?.caseScope || safeScope,
  };
}

export const resolveGoodsStudioCaseOwnerLabel = resolveText2ImgCaseOwnerLabel;
export const buildGoodsStudioCaseOwnerFilterOptions = buildText2ImgCaseOwnerFilterOptions;

export { resolveVisibleTitlePresetScopes, DEFAULT_TITLE_PRESET_SCOPE };

/** 工作流执行：优先用 params 内字段，否则按模板 ID 拉取商品信息与地区设置 */
export async function resolveGoodsStudioWorkflowParams(params = {}) {
  let productInfo = String(params.productInfo || '').trim();
  let country = normalizeCountry(params.country);
  let countryStyle = normalizeGoodsStudioCountryStyle(country, params.countryStyle);
  let language = normalizeLanguage(params.language);
  let mainImageRatio = normalizeMainImageRatio(params.mainImageRatio);
  const caseId = String(params.goodsStudioPromptCaseId || '').trim();
  if (!productInfo && caseId) {
    try {
      const res = await aiGoodsStudioPromptCaseAPI.getById(caseId);
      const mapped = mapGoodsStudioPromptCaseApiItem(res?.case);
      if (mapped) {
        productInfo = String(mapped.productInfo || '').trim();
        country = normalizeCountry(mapped.country);
        countryStyle = normalizeGoodsStudioCountryStyle(country, mapped.countryStyle);
        language = normalizeLanguage(mapped.language);
        mainImageRatio = normalizeMainImageRatio(mapped.mainImageRatio);
      }
    } catch {
      // ignore
    }
  }
  return { productInfo, country, countryStyle, language, mainImageRatio };
}
